import React, { useState, useEffect } from "react";
import { Box, Typography, TextField, Button } from "@mui/material";
import { makeStyles } from "@material-ui/core";
import RentPeriodSelector from "../molecules/specialCondition/adddeposit/rentPeriodSelector";
import { TextInput } from "../atoms/textinput";
import moment from "moment";
import getPlatformType from "../../../../helpers/helpers";
import ExpectedEndDateSelector from "../molecules/specialCondition/adddeposit/ExpectedEndDateSelector";
import { Controller } from "react-hook-form";

const useStyles = makeStyles(() => ({
  label: {
    fontSize: 16,
    color: "#6B6B80",
    marginBottom: 6,
    fontWeight: 500,
  },

  textField: {
    "& .MuiOutlinedInput-root": {
      height: 46,
      borderRadius: 10,
      fontSize: 16,
      paddingLeft: 10,
      background: "#fff",
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "#D3D3E0",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "#BEBED1",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "#9C6CFF",
    },
    width: "100%",
  },

  currencyBox: {
    height: 46,
    border: "1px solid #D3D3E0",
    borderRadius: 10,
    paddingLeft: 14,
    display: "flex",
    alignItems: "center",
    background: "#fff",
  },

  currencyInput: {
    flex: 1,
    border: "none",
    outline: "none",
    marginLeft: 10,
    fontSize: 16,
    color: "#444",
  },

  viewBtn: {
    background: "#2CA7C2",
    color: "#fff",
    height: 46,
    borderRadius: 8,
    padding: "0 18px",
    fontWeight: 600,
    fontSize: 14,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    whiteSpace: "nowrap",
    boxShadow: "0px 2px 6px rgba(0,0,0,0.08)",
    "&:hover": {
      background: "#2396AE",
    },
  },
}));

const layoutType = getPlatformType();

const DepositDetails = ({
  control,
  watch,
  setValue,
  errors,
  onViewProperty,
  openFlow,
  endDateOption,
  payload
}) => {
  const classes = useStyles();

  const tenancyStart = watch("tenancy_start_date");

  // AUTO UPDATE END DATE BASED ON SELECTED OPTION
  useEffect(() => {
    if (endDateOption !== "custom" && tenancyStart) {
      const monthsToAdd = parseInt(endDateOption);
      const newDate = moment(tenancyStart).add(monthsToAdd, "months").toDate();
      setValue("tenancy_end_date", newDate);
    }
  }, [endDateOption, tenancyStart]);

  // MAP openFlow VALUES
  useEffect(() => {
    if (openFlow) {
      const mappedValues = {
        ...payload,
        rental_amount: openFlow?.open_flow_rental_offer?.rental_amount || "",
        rental_frequency:
          openFlow?.open_flow_rental_offer?.rental_frequency || "PCM",
        tenancy_deposit_amount:
          openFlow?.open_flow_rental_offer?.tenancy_deposit_amount || "",
        tenancy_deposit_amount_protect:
          openFlow?.open_flow_rental_offer?.tenancy_deposit_amount || "",
        holding_desposit_received_date: openFlow?.open_flow_holding_deposit
          ?.holding_desposit_received_date
          ? new Date(
              openFlow.open_flow_holding_deposit.holding_desposit_received_date
            )
          : new Date(),
        tenancy_start_date: openFlow?.open_flow_rental_offer?.tenancy_start_date
          ? new Date(openFlow.open_flow_rental_offer.tenancy_start_date)
          : new Date(),
        tenancy_end_date: openFlow?.open_flow_rental_offer?.tenancy_end_date
          ? new Date(openFlow.open_flow_rental_offer.tenancy_end_date)
          : new Date(),
        number_of_tenants: openFlow?.tenants?.length || 1,
        property_name: openFlow?.property?.property_name || "",
      };

      Object.entries(mappedValues).forEach(([key, value]) => {
        setValue(key, value);
      });
    }
  }, [openFlow]);

  const rentPeriodItems = [
    { value: "week", label: "Weekly" },
    { value: "monthly", label: "Per Calender Month" },
    { value: "quarterly", label: "Quarterly" },
    { value: "6 monthly", label: "6 Monthly" },
    { value: "annually", label: "Annually" },
]

  return (
    <Box
      width="100%"
      maxWidth="900px"
      display="flex"
      flexDirection="column"
      gap={1}
    >
      {/* PROPERTY NAME + VIEW PROPERTY */}
      <Box display="flex" justifyContent="space-between" alignItems="center" gap={2}>
        <Box flex={1}>
          <Typography className={classes.label}>Property Name</Typography>

          <Controller
            name="property_name"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                placeholder="Enter property name"
                className={classes.textField}
                error={!!errors.property_name}
              />
            )}
          />
          {errors.property_name && (
            <Typography color="error" fontSize={12}>
              {errors.property_name.message}
            </Typography>
          )}
        </Box>

        <Button className={classes.viewBtn} onClick={onViewProperty}>
          VIEW PROPERTY
        </Button>
      </Box>

      {/* RENT AMOUNT */}
      <Box>
        <Typography className={classes.label}>Rent Amount</Typography>

        <Controller
          name="rental_amount"
          control={control}
          render={({ field }) => (
            <>
              <Box className={classes.currencyBox}>
                <Typography style={{ fontSize: 20, color: "#777" }}>£</Typography>
                <input
                  {...field}
                  className={classes.currencyInput}
                  type="number"
                  placeholder="0.00"
                />
              </Box>
            </>
          )}
        />
        {errors.rental_amount && (
          <Typography color="error" fontSize={12}>
            {errors.rental_amount.message}
          </Typography>
        )}
      </Box>

      {/* RENT PERIOD SELECTOR */}
      <RentPeriodSelector
        heading="Rent Period"
        items={rentPeriodItems}
        selected={watch("rental_frequency")}
        onSelection={(val) => setValue("rental_frequency", val)}
        error={errors.rental_frequency}
      />
      {errors.rental_frequency && (
        <Typography color="error" fontSize={12}>
          {errors.rental_frequency.message}
        </Typography>
      )}

      {/* DEPOSIT AMOUNT */}
      <Box>
        <Typography className={classes.label}>Deposit Amount</Typography>
        <Controller
          name="tenancy_deposit_amount"
          control={control}
          render={({ field }) => (
            <>
              <Box className={classes.currencyBox}>
                <Typography style={{ fontSize: 20, color: "#777" }}>£</Typography>
                <input
                  {...field}
                  className={classes.currencyInput}
                  type="number"
                  placeholder="0.00"
                />
              </Box>
            </>
          )}
        />

        {errors.tenancy_deposit_amount && (
          <Typography color="error" fontSize={12}>
            {errors.tenancy_deposit_amount.message}
          </Typography>
        )}
      </Box>

      {/* AMOUNT TO PROTECT */}
      <Box>
        <Typography className={classes.label}>Amount to protect</Typography>
        <Controller
          name="tenancy_deposit_amount_protect"
          control={control}
          render={({ field }) => (
            <>
              <Box className={classes.currencyBox}>
                <Typography style={{ fontSize: 20, color: "#777" }}>£</Typography>
                <input
                  {...field}
                  className={classes.currencyInput}
                  type="number"
                  placeholder="0.00"
                />
              </Box>
            </>
          )}
        />

        {errors.tenancy_deposit_amount_protect && (
          <Typography color="error" fontSize={12}>
            {errors.tenancy_deposit_amount_protect.message}
          </Typography>
        )}
      </Box>

     {/* EXPECTED END DATE SELECTOR */}
<Box>
  <Typography className={classes.label}>Expected End Date</Typography>

  <ExpectedEndDateSelector
  initialDate={watch("tenancy_start_date")}
  formEndDate={watch("tenancy_end_date")}   // ← ADD THIS
  onSelect={(info) => {
    setValue("endDateOption", info.option);

    if (info.option !== "custom") {
      setValue("tenancy_end_date", info.date);
    }
  }}
/>


  {watch("endDateOption") === "custom" && (
    <Box mt={2}>
      <Controller
        name="tenancy_end_date"
        control={control}
        render={({ field }) => (
          <TextInput
            {...field}
            value={moment(field.value).format("YYYY-MM-DD")}
            title="Expected End Date"
            type="date"
            isfullwidth={true}
          />
        )}
      />
      {errors.tenancy_end_date && (
        <Typography color="error" fontSize={12}>
          {errors.tenancy_end_date.message}
        </Typography>
      )}
    </Box>
  )}
</Box>


      {/* DATE ROWS */}
      <Box
        display="flex"
        flexDirection={layoutType === "phone" ? "column" : "row"}
        gap={2}
      >
        {/* START DATE */}
        <Box flex={1}>
          <Controller
            name="tenancy_start_date"
            control={control}
            render={({ field }) => (
              <TextInput
                {...field}
                value={moment(field.value).format('YYYY-MM-DD')}
                title="Tenancy start date"
                isfullwidth={true}
                type="date"
              />
            )}
          />

          {errors.tenancy_start_date && (
            <Typography color="error" fontSize={12}>
              {errors.tenancy_start_date.message}
            </Typography>
          )}
        </Box>

        {/* END DATE */}
        <Box flex={1}>
          <Controller
            name="holding_desposit_received_date"
            control={control}
            render={({ field }) => (
              <TextInput
                {...field}
                value={moment(field.value).format('YYYY-MM-DD')}
                title="Deposit Received Date"
                type="date"
                isfullwidth={true}
              />
            )}
          />

          {errors.tenancy_end_date && (
            <Typography color="error" fontSize={12}>
              {errors.tenancy_end_date.message}
            </Typography>
          )}
        </Box>
      </Box>

      {/* NUMBER OF TENANTS */}
      <Box maxWidth="300px">
        <Typography className={classes.label}>Number of tenants</Typography>

        <Controller
          name="number_of_tenants"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              placeholder="Enter Number of tenants"
              className={classes.textField}
              type="number"
            />
          )}
        />

        {errors.number_of_tenants && (
          <Typography color="error" fontSize={12}>
            {errors.number_of_tenants.message}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default DepositDetails;
